/**
 * QuestService — the daily/weekly quests over StorageLike. Progress is derived
 * from a snapshot of lifetime counters against each period's baseline (see
 * config/quests); this service reads the snapshot, materialises periods,
 * pays claims and bumps counters in the right order.
 * Spec: docs/superpowers/specs/2026-09-08-quests-and-potions-design.md §2.
 */
import type { StorageLike } from '../account/saveSync';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../config/levels';
import {
  QUEST_PERIODS,
  QUEST_REWARD,
  QUESTS_KEY,
  claimQuest,
  currentPeriod,
  nextDailyStreak,
  parseQuests,
  questRows,
  type QuestPeriod,
  type QuestRow,
  type QuestSnapshot,
  type QuestState,
} from '../config/quests';
import type { StatField } from '../config/lifetimeStats';
import { ProgressService } from './progressService';
import { RankService } from './rankService';
import { StatsService } from './statsService';

export class QuestService {
  private readonly progress: ProgressService;
  private readonly rank: RankService;
  private readonly stats: StatsService;

  constructor(
    private readonly storage: StorageLike,
    /** The signed-in user id or the device id — seeds which set a player gets. */
    private readonly salt: string,
  ) {
    this.progress = new ProgressService(storage, LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.rank = new RankService(storage);
    this.stats = new StatsService(storage);
  }

  /** Every metric the quests can measure, as it stands right now. */
  snapshot(): QuestSnapshot {
    return {
      ...this.stats.stats(),
      waves: this.stats.waves(),
      rankUps: this.rank.rank(),
      achievements: this.rank.earned().length,
    };
  }

  state(): QuestState {
    return parseQuests(this.storage.getItem(QUESTS_KEY));
  }

  private save(state: QuestState): void {
    this.storage.setItem(QUESTS_KEY, JSON.stringify(state));
  }

  /** Materialise both periods for `now` (persisting only when something rolled). */
  ensure(now: number): QuestState {
    const before = this.state();
    const snap = this.snapshot();
    const after: QuestState = { ...before };
    let changed = false;
    for (const p of QUEST_PERIODS) {
      const cur = currentPeriod(before[p], p, now, this.salt, snap);
      if (cur !== before[p]) {
        after[p] = cur;
        changed = true;
      }
    }
    if (changed) this.save(after);
    return after;
  }

  rows(period: QuestPeriod, now: number): QuestRow[] {
    const ps = this.ensure(now)[period];
    return ps ? questRows(ps, period, this.snapshot()) : [];
  }

  /** Pay quest `i` of `period`: coins + exp, once. Null when not claimable. */
  claim(period: QuestPeriod, i: number, now: number): { coins: number; exp: number } | null {
    const state = this.ensure(now);
    const ps = state[period];
    if (!ps) return null;
    const next = claimQuest(ps, period, i, this.snapshot());
    if (!next) return null;
    const saved: QuestState = { ...state, [period]: next };
    // The third daily claim of a day extends the Quester streak.
    if (period === 'daily' && next.claimed.every(Boolean)) {
      saved.streak = nextDailyStreak(state.streak, next.index);
    }
    this.save(saved);
    const reward = QUEST_REWARD[period];
    this.progress.addCoins(reward.coins);
    this.rank.addExp(reward.exp);
    return reward;
  }

  /**
   * Bump a lifetime counter. `ensure` runs FIRST so a period that rolled over
   * since the last visit takes its baseline before the bump — otherwise the
   * first kill of a new day would vanish into the baseline.
   */
  record(field: StatField, n: number, now: number): void {
    this.ensure(now);
    this.stats.inc(field, n);
  }
}
